import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from '../components/StripePaymentForm';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';
import AuthModal from '../components/AuthModal';
import axios from 'axios';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

/**
 * US State name to abbreviation mapping
 */
const STATE_ABBREVIATIONS = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
  'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
  'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
  'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
  'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
  'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
  'Wisconsin': 'WI', 'Wyoming': 'WY'
};

/**
 * Booking Flow component for creating new bookings.
 * 
 * This component provides a multi-step booking process including:
 * 1. Route selection and search
 * 2. Shipment details and bag information
 * 3. Pickup and delivery information
 * 4. Cost calculation and confirmation
 * 
 * @author BagABoard Team
 */
function BookingFlow() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [costCalculation, setCostCalculation] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Page navigation state - React convention with 'show' prefix
  const [showPricingPage, setShowPricingPage] = useState(false);
  const [showItemsPage, setShowItemsPage] = useState(false);
  const [showCustomsAndInsurancePage, setShowCustomsAndInsurancePage] = useState(false);
  const [showFromAddressPage, setShowFromAddressPage] = useState(false);
  const [showToAddressPage, setShowToAddressPage] = useState(false);
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [showDatesPage, setShowDatesPage] = useState(false);
  const [showEssentialsPage, setShowEssentialsPage] = useState(false);
  const [showConfirmOrderPage, setShowConfirmOrderPage] = useState(false);
  const [journeyType, setJourneyType] = useState('one-way');
  const [flexiCover, setFlexiCover] = useState('');
  const [trackingPhone, setTrackingPhone] = useState('');
  const [collectionDate, setCollectionDate] = useState(null);
  const [deliveryService, setDeliveryService] = useState('standard');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [collectionTime, setCollectionTime] = useState('');
  const [labelHolders, setLabelHolders] = useState('');
  const [whyUsingUs, setWhyUsingUs] = useState('');
  const [agreeCustoms, setAgreeCustoms] = useState(false);
  const [agreeAerosols, setAgreeAerosols] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeRouteNotes, setAgreeRouteNotes] = useState(false);
  const [billingAddressOption, setBillingAddressOption] = useState('');
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [billingZipCode, setBillingZipCode] = useState('');
  
  // Feature flag for test defaults (only active in development)
  const USE_TEST_DEFAULTS = process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_TEST_DEFAULTS === 'true';
  
  const [items, setItems] = useState([]);
  const [customsDeclaration, setCustomsDeclaration] = useState(USE_TEST_DEFAULTS ? 'personal_own' : '');
  const [declaredValue, setDeclaredValue] = useState(USE_TEST_DEFAULTS ? '300' : '');
  const [coverLevel, setCoverLevel] = useState(USE_TEST_DEFAULTS ? '200' : '');
  const [customCoverageAmount, setCustomCoverageAmount] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [addressType, setAddressType] = useState(USE_TEST_DEFAULTS ? 'residence' : '');
  const [pickupContact, setPickupContact] = useState(USE_TEST_DEFAULTS ? 'aa bb' : '');
  const [pickupPhone, setPickupPhone] = useState(USE_TEST_DEFAULTS ? '4082026317' : '');
  const [pickupZipCode, setPickupZipCode] = useState(USE_TEST_DEFAULTS ? '94043' : '');
  const [pickupHotelName, setPickupHotelName] = useState('');
  const [pickupAddressLine1, setPickupAddressLine1] = useState(USE_TEST_DEFAULTS ? '162 Cottonwood Ct' : '');
  const [pickupAddressLine2, setPickupAddressLine2] = useState('');
  const [pickupCity, setPickupCity] = useState(USE_TEST_DEFAULTS ? 'Mountain View' : '');
  const [pickupState, setPickupState] = useState(USE_TEST_DEFAULTS ? 'California' : '');
  const [pickupCountry, setPickupCountry] = useState('United States');
  const [deliveryAddressType, setDeliveryAddressType] = useState(USE_TEST_DEFAULTS ? 'residence' : '');
  const [deliveryContact, setDeliveryContact] = useState(USE_TEST_DEFAULTS ? 'rr ss' : '');
  const [deliveryPhone, setDeliveryPhone] = useState(USE_TEST_DEFAULTS ? '4082026317' : '');
  const [deliveryZipCode, setDeliveryZipCode] = useState(USE_TEST_DEFAULTS ? '10001' : '');
  const [deliveryHotelName, setDeliveryHotelName] = useState('');
  const [deliveryAddressLine1, setDeliveryAddressLine1] = useState(USE_TEST_DEFAULTS ? '20 Cooper Square' : '');
  const [deliveryAddressLine2, setDeliveryAddressLine2] = useState('');
  const [deliveryCity, setDeliveryCity] = useState(USE_TEST_DEFAULTS ? 'Rochester' : '');
  const [deliveryState, setDeliveryState] = useState(USE_TEST_DEFAULTS ? 'New York' : '');
  const [deliveryCountry, setDeliveryCountry] = useState('United States');
  const [currentItem, setCurrentItem] = useState({
    width: USE_TEST_DEFAULTS ? '18' : '',
    height: USE_TEST_DEFAULTS ? '10' : '',
    length: USE_TEST_DEFAULTS ? '26' : '',
    weight: USE_TEST_DEFAULTS ? '22' : '',
    itemType: USE_TEST_DEFAULTS ? 'Suitcase' : '',
    description: '',
    unit: 'imperial' // imperial or metric
  });
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Route Selection
    originCountry: 'USA',
    originCity: '',
    destinationCountry: 'USA',
    destinationCity: '',
    
    // Step 2: Shipment Details
    numberOfBags: 1,
    totalWeight: '',
    specialHandling: false,
    insurance: false,
    
    // Step 3: Pickup & Delivery
    pickupAddress: '',
    pickupCity: '',
    pickupPostalCode: '',
    pickupDate: '',
    pickupTimeSlot: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryPostalCode: '',
    recipientName: '',
    recipientPhone: '',
    recipientEmail: ''
  });

  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState({ origins: [], destinations: [] });
  const [cities, setCities] = useState({ origins: {}, destinations: {} });
  const [upsRates, setUpsRates] = useState(null);
  const [upsRatesWithoutInsurance, setUpsRatesWithoutInsurance] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Show Add Items page only when coming from route-prices with a selected service level
  useEffect(() => {
    if (location.state?.serviceLevel) {
      // Coming from route-prices page with a service level selected
      setShowItemsPage(true);
      setDeliveryService(location.state.serviceLevel);
      
      // Update form data with the selected states
      if (location.state.fromState && location.state.toState) {
        const fromStateAbbr = STATE_ABBREVIATIONS[location.state.fromState] || location.state.fromState;
        const toStateAbbr = STATE_ABBREVIATIONS[location.state.toState] || location.state.toState;
        
        setFormData(prev => ({
          ...prev,
          originCity: location.state.fromState,
          destinationCity: location.state.toState,
          originCountry: location.state.fromCountry || 'United States',
          destinationCountry: location.state.toCountry || 'United States'
        }));
        
        // Also set pickup and delivery states for the booking (use full state names for UPS rates)
        setPickupState(location.state.fromState);
        setDeliveryState(location.state.toState);
        
        // Fetch the actual route from the backend
        const fetchRoute = async () => {
          try {
            const originCountry = location.state.fromCountry === 'United States' ? 'USA' : location.state.fromCountry;
            const destinationCountry = location.state.toCountry === 'United States' ? 'USA' : location.state.toCountry;
            
            const routesResponse = await apiService.routes.searchRoutes(originCountry, destinationCountry);
            const routes = Array.isArray(routesResponse) ? routesResponse : (routesResponse.routes || []);
            
            // Find route matching service level and states
            const matchingRoute = routes.find(r => 
              r.serviceLevel?.toUpperCase() === location.state.serviceLevel.toUpperCase() &&
              r.originState === fromStateAbbr &&
              r.destinationState === toStateAbbr
            );
            
            if (matchingRoute) {
              setSelectedRoute({
                id: matchingRoute.id || matchingRoute.routeId,
                serviceLevel: location.state.serviceLevel.toUpperCase(),
                basePrice: matchingRoute.basePrice || (location.state.serviceLevel === 'standard' ? '71.00' : '151.00')
              });
            } else {
              toast.error(`No route found from ${location.state.fromState} to ${location.state.toState}. Please contact support.`);
            }
          } catch (error) {
            console.error('Error fetching route:', error);
            toast.error('Failed to load route information. Please try again.');
          }
        };
        
        fetchRoute();
      }
    } else {
      // Start at route selection page
      setShowItemsPage(false);
      setDeliveryService('standard');
      setSelectedRoute(null);
    }
  }, [location]);

  // Allow booking without authentication (removed redirect)
  // useEffect(() => {
  //   if (!isAuthenticated()) {
  //     navigate('/login');
  //   }
  // }, [isAuthenticated, navigate]);

  // Load countries on component mount
  useEffect(() => {
    loadCountries();
  }, []);

  // Check for pending booking after login
  useEffect(() => {
    const checkPendingBooking = async () => {
      if (isAuthenticated() && pendingBookingData) {
        console.log('User authenticated, restoring pending booking');
        // Clear the pending booking
        setPendingBookingData(null);
        localStorage.removeItem('pendingBooking');
        // The booking will be completed when the user submits payment again
        toast.info('Please submit your payment to complete the booking');
      }
    };
    
    checkPendingBooking();
  }, [isAuthenticated, pendingBookingData]);

  // Fetch UPS rates when pickup and delivery states are set
  useEffect(() => {
    const fetchUpsRates = async () => {
      if (!pickupState || !deliveryState) {
        return;
      }

      const STATE_ZIP_MAP = {
        'California': { city: 'Los Angeles', state: 'CA', zip: '90001' },
        'New York': { city: 'New York', state: 'NY', zip: '10001' },
        'Texas': { city: 'Houston', state: 'TX', zip: '77001' },
        'Florida': { city: 'Miami', state: 'FL', zip: '33101' }
      };

      // For same-state shipping, use different cities within the state
      const STATE_ALTERNATE_ZIP_MAP = {
        'California': { city: 'San Francisco', state: 'CA', zip: '94102' },
        'New York': { city: 'Buffalo', state: 'NY', zip: '14201' },
        'Texas': { city: 'Dallas', state: 'TX', zip: '75201' },
        'Florida': { city: 'Jacksonville', state: 'FL', zip: '32099' }
      };

      const fromInfo = STATE_ZIP_MAP[pickupState];
      const toInfo = pickupState === deliveryState 
        ? STATE_ALTERNATE_ZIP_MAP[deliveryState]  // Use alternate city for same-state
        : STATE_ZIP_MAP[deliveryState];

      if (!fromInfo || !toInfo) {
        return;
      }

      setLoadingRates(true);
      try {
        // Fetch rates for common weight tiers
        const weights = ['23', '50', '73'];
        const ratesByWeight = {};
        const ratesWithoutInsurance = {};

        for (const weight of weights) {
          const params = {
            fromCity: fromInfo.city,
            fromState: fromInfo.state,
            fromZip: fromInfo.zip,
            toCity: toInfo.city,
            toState: toInfo.state,
            toZip: toInfo.zip,
            weight: weight
          };

          // First, always fetch base rate without insurance
          const baseResponse = await axios.get('/api/bookings/ups-rates', {
            params: params
          });

          if (baseResponse.data.success && baseResponse.data.rates) {
            const groundRate = baseResponse.data.rates.find(r => r.Service.Code === '03');
            const expressRate = baseResponse.data.rates.find(r => r.Service.Code === '12');

            ratesWithoutInsurance[weight] = {
              ground: groundRate ? parseFloat(groundRate.TotalCharges.MonetaryValue) : null,
              express: expressRate ? parseFloat(expressRate.TotalCharges.MonetaryValue) : null
            };
          }

          // Then fetch with insurance if coverage is selected
          if (coverLevel && coverLevel !== 'none' && coverLevel !== '') {
            // Use custom amount if 'custom' is selected, otherwise use the preset tier
            params.declaredValue = coverLevel === 'custom' ? customCoverageAmount : coverLevel;

            const response = await axios.get('/api/bookings/ups-rates', {
              params: params
            });

            if (response.data.success && response.data.rates) {
              const groundRate = response.data.rates.find(r => r.Service.Code === '03');
              const expressRate = response.data.rates.find(r => r.Service.Code === '12');

              ratesByWeight[weight] = {
                ground: groundRate ? parseFloat(groundRate.TotalCharges.MonetaryValue) : null,
                express: expressRate ? parseFloat(expressRate.TotalCharges.MonetaryValue) : null
              };
            }
          } else {
            // No coverage, use base rates
            ratesByWeight[weight] = ratesWithoutInsurance[weight];
          }
        }

        setUpsRates(ratesByWeight);
        setUpsRatesWithoutInsurance(ratesWithoutInsurance);
      } catch (error) {
        console.error('Error fetching UPS rates:', error);
      } finally {
        setLoadingRates(false);
      }
    };

    fetchUpsRates();
  }, [pickupState, deliveryState, coverLevel, customCoverageAmount]);

  /**
   * Load available countries and cities for origin and destination
   */
  const loadCountries = async () => {
    try {
      const [originCities, destinationCities] = await Promise.all([
        fetch('/api/routes/cities/origins').then(res => res.json()),
        fetch('/api/routes/cities/destinations').then(res => res.json())
      ]);
      
      const originCountries = Object.keys(originCities.citiesByCountry || {}).sort();
      const destinationCountries = Object.keys(destinationCities.citiesByCountry || {}).sort();
      
      setCountries({
        origins: originCountries,
        destinations: destinationCountries
      });
      
      setCities({
        origins: originCities.citiesByCountry || {},
        destinations: destinationCities.citiesByCountry || {}
      });
    } catch (error) {
      console.error('Error loading countries:', error);
      toast.error('Failed to load countries. Please refresh the page.');
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Show prices for selected route
   */
  const showPrices = async () => {
    if (!formData.originCity || !formData.destinationCity) {
      setErrors({
        originCity: !formData.originCity ? 'Please select origin city' : '',
        destinationCity: !formData.destinationCity ? 'Please select destination city' : ''
      });
      return;
    }

    setLoading(true);
    setRoutes([]); // Clear previous routes
    setSelectedRoute(null);
    
    try {
      const response = await apiService.routes.searchRoutes(
        formData.originCountry,
        formData.destinationCountry
      );
      
      // Filter routes by selected cities
      const filteredRoutes = (response.routes || []).filter(route => 
        route.originCity === formData.originCity && route.destinationCity === formData.destinationCity
      );
      
      setRoutes(filteredRoutes);
      
      if (filteredRoutes.length === 0) {
        toast.info('No routes found for the selected cities. Please try different locations.');
      } else {
        // Show pricing page
        setShowPricingPage(true);
        // Don't auto-select, let user choose between Standard and Express
      }
    } catch (error) {
      console.error('Route search error:', error);
      toast.error('Failed to load prices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Go back to route selection
   */
  const backToRouteSelection = () => {
    setShowPricingPage(false);
    setRoutes([]);
    setSelectedRoute(null);
    setCostCalculation(null);
  };

  /**
   * Select a route and show Add Items page
   */
  const selectRoute = async (route) => {
    setSelectedRoute(route);
    setShowPricingPage(false);
    setShowItemsPage(true);
  };

  /**
   * Add item to the list
   */
  const addItem = () => {
    if (!currentItem.width || !currentItem.height || !currentItem.length || !currentItem.weight) {
      toast.error('Please fill in all dimensions and weight');
      return;
    }
    
    setItems([...items, { ...currentItem, id: Date.now() }]);
    setCurrentItem({
      width: USE_TEST_DEFAULTS ? '18' : '',
      height: USE_TEST_DEFAULTS ? '10' : '',
      length: USE_TEST_DEFAULTS ? '26' : '',
      weight: USE_TEST_DEFAULTS ? '22' : '',
      itemType: USE_TEST_DEFAULTS ? 'Suitcase' : '',
      description: '',
      unit: currentItem.unit
    });
    toast.success('Item added!');
  };

  /**
   * Remove item from list
   */
  const removeItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  /**
   * Continue to customs page after adding items
   */
  const continueFromAddItems = () => {
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    // Calculate total weight
    const totalWeight = items.reduce((sum, item) => {
      const weight = parseFloat(item.weight);
      // Convert to kg if imperial
      return sum + (item.unit === 'imperial' ? weight * 0.453592 : weight);
    }, 0);
    
    setFormData(prev => ({
      ...prev,
      numberOfBags: items.length,
      totalWeight: totalWeight.toFixed(2)
    }));
    
    setShowItemsPage(false);
    setShowCustomsAndInsurancePage(true);
    setCurrentStep(2);
  };

  /**
   * Recalculate cost when weight or bag count changes
   */
  const recalculateCost = async () => {
    if (selectedRoute && formData.numberOfBags && formData.totalWeight) {
      try {
        const cost = await apiService.routes.calculateCost(
          selectedRoute.id,
          parseFloat(formData.totalWeight),
          parseInt(formData.numberOfBags)
        );
        setCostCalculation(cost);
      } catch (error) {
        console.error('Cost recalculation error:', error);
      }
    }
  };

  /**
   * Validate current step
   */
  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.originCountry) newErrors.originCountry = 'Please select origin country';
        if (!formData.destinationCountry) newErrors.destinationCountry = 'Please select destination country';
        if (!selectedRoute) newErrors.route = 'Please select a route';
        break;
        
      case 2:
        if (!formData.numberOfBags || formData.numberOfBags < 1) {
          newErrors.numberOfBags = 'Please enter number of bags';
        }
        if (!formData.totalWeight || formData.totalWeight <= 0) {
          newErrors.totalWeight = 'Please enter total weight';
        }
        break;
        
      case 3:
        if (!formData.pickupAddress) newErrors.pickupAddress = 'Pickup address is required';
        if (!formData.pickupCity) newErrors.pickupCity = 'Pickup city is required';
        if (!formData.pickupPostalCode) newErrors.pickupPostalCode = 'Pickup postal code is required';
        if (!formData.pickupDate) newErrors.pickupDate = 'Pickup date is required';
        if (!formData.pickupTimeSlot) newErrors.pickupTimeSlot = 'Pickup time slot is required';
        
        if (!formData.deliveryAddress) newErrors.deliveryAddress = 'Delivery address is required';
        if (!formData.deliveryCity) newErrors.deliveryCity = 'Delivery city is required';
        if (!formData.deliveryPostalCode) newErrors.deliveryPostalCode = 'Delivery postal code is required';
        if (!formData.recipientName) newErrors.recipientName = 'Recipient name is required';
        if (!formData.recipientPhone) newErrors.recipientPhone = 'Recipient phone is required';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Move to next step
   */
  const nextStep = () => {
    if (showPricingPage) {
      // From pricing page, go to step 3 (addresses)
      setShowPricingPage(false);
      setCurrentStep(3);
    } else if (validateStep(currentStep)) {
      if (currentStep === 2) {
        recalculateCost();
      }
      setCurrentStep(prev => prev + 1);
    }
  };

  /**
   * Move to previous step
   */
  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  /**
   * Submit booking
   */
  const submitBooking = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      const bookingData = {
        routeId: selectedRoute.id,
        numberOfBags: parseInt(formData.numberOfBags),
        totalWeight: parseFloat(formData.totalWeight),
        pickupAddress: formData.pickupAddress,
        pickupCity: formData.pickupCity,
        pickupPostalCode: formData.pickupPostalCode,
        pickupDate: formData.pickupDate,
        pickupTimeSlot: formData.pickupTimeSlot,
        deliveryAddress: formData.deliveryAddress,
        deliveryCity: formData.deliveryCity,
        deliveryPostalCode: formData.deliveryPostalCode,
        recipientName: formData.recipientName,
        recipientPhone: formData.recipientPhone,
        recipientEmail: formData.recipientEmail || null,
        specialHandling: formData.specialHandling,
        insurance: formData.insurance
      };

      const booking = await apiService.bookings.createBooking(bookingData);
      toast.success('Booking created successfully!');
      navigate(`/booking-confirmation/${booking.id}`);
    } catch (error) {
      console.error('Booking creation error:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date for pickup (tomorrow)
  const getMinPickupDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={10}>
          {/* Show add items page, customs page, address page, delivery address page, review page, dates page, essentials page, confirm order page, or pricing page */}
          {showItemsPage ? (
            // Add Items Page
            <>
              {/* Back Button */}
              <Button 
                variant="link" 
                className="text-primary mb-3 p-0" 
                onClick={() => navigate('/route-prices')}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </Button>

              <Row>
                {/* Left Column - Add Items Form */}
                <Col lg={7}>
              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-light">
                  <h3 className="mb-0">Add Items</h3>
                </Card.Header>
                <Card.Body>
                  {/* Measurement Units Toggle */}
                  <div className="mb-4">
                    <strong className="me-3">Measurement Units</strong>
                    <Form.Check
                      inline
                      type="radio"
                      label="Metric (cm/kg)"
                      name="unit"
                      value="metric"
                      checked={currentItem.unit === 'metric'}
                      onChange={(e) => setCurrentItem({...currentItem, unit: 'metric'})}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Imperial (inches/lbs)"
                      name="unit"
                      value="imperial"
                      checked={currentItem.unit === 'imperial'}
                      onChange={(e) => setCurrentItem({...currentItem, unit: 'imperial'})}
                    />
                  </div>

                  <Row>
                    {/* Width */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="fas fa-ruler-horizontal me-2 text-primary"></i>
                          Width
                        </Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            value={currentItem.width}
                            onChange={(e) => setCurrentItem({...currentItem, width: e.target.value})}
                            placeholder="18"
                            min="0"
                            step="0.1"
                            style={USE_TEST_DEFAULTS && currentItem.width === '18' ? { color: '#999' } : {}}
                          />
                          <span className="input-group-text bg-light">
                            {currentItem.unit === 'imperial' ? 'in' : 'cm'}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>

                    {/* Height */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="fas fa-ruler-vertical me-2 text-primary"></i>
                          Height
                        </Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            value={currentItem.height}
                            onChange={(e) => setCurrentItem({...currentItem, height: e.target.value})}
                            placeholder="10"
                            min="0"
                            step="0.1"
                            style={USE_TEST_DEFAULTS && currentItem.height === '10' ? { color: '#999' } : {}}
                          />
                          <span className="input-group-text bg-light">
                            {currentItem.unit === 'imperial' ? 'in' : 'cm'}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>

                    {/* Length */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="fas fa-ruler me-2 text-primary"></i>
                          Length
                        </Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            value={currentItem.length}
                            onChange={(e) => setCurrentItem({...currentItem, length: e.target.value})}
                            placeholder="26"
                            min="0"
                            step="0.1"
                            style={USE_TEST_DEFAULTS && currentItem.length === '26' ? { color: '#999' } : {}}
                          />
                          <span className="input-group-text bg-light">
                            {currentItem.unit === 'imperial' ? 'in' : 'cm'}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>

                    {/* Weight */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="fas fa-weight me-2 text-primary"></i>
                          Weight
                        </Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            value={currentItem.weight}
                            onChange={(e) => setCurrentItem({...currentItem, weight: e.target.value})}
                            placeholder="22"
                            min="0"
                            style={USE_TEST_DEFAULTS && currentItem.weight === '22' ? { color: '#999' } : {}}
                            step="0.1"
                          />
                          <span className="input-group-text bg-light">
                            {currentItem.unit === 'imperial' ? 'lb' : 'kg'}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Item Type */}
                  <Form.Group className="mb-3">
                    <Form.Label>Item Type</Form.Label>
                    <Form.Select
                      value={currentItem.itemType}
                      onChange={(e) => setCurrentItem({...currentItem, itemType: e.target.value})}
                    >
                      <option value="">Please Select</option>
                      <option value="suitcase">Suitcase</option>
                      <option value="box">Box</option>
                      <option value="bag">Bag</option>
                      <option value="sports_equipment">Sports Equipment</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>

                  {/* Item Description */}
                  <Form.Group className="mb-4">
                    <Form.Label>Item Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentItem.description}
                      onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
                      placeholder="e.g. Color, Make"
                    />
                  </Form.Group>

                  <div className="text-end">
                    <Button
                      variant="outline-success"
                      size="lg"
                      onClick={addItem}
                      className="px-5"
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add Item
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Items List - Grid Display */}
              {items.length > 0 && (
                <div className="mb-4">
                  <Row className="mb-3">
                    {items.map((item, index) => {
                      // Get icon based on item type
                      const getItemIcon = (type) => {
                        switch(type) {
                          case 'suitcase': return 'fa-suitcase-rolling';
                          case 'box': return 'fa-box';
                          case 'bag': return 'fa-shopping-bag';
                          case 'sports_equipment': return 'fa-dumbbell';
                          default: return 'fa-box';
                        }
                      };

                      return (
                        <Col md={6} lg={6} key={item.id} className="mb-3">
                          <Card className="h-100 border-primary border-2 shadow-sm">
                            <Card.Body className="text-center">
                              <div className="mb-3">
                                <i className={`fas ${getItemIcon(item.itemType)} fa-3x text-primary`}></i>
                              </div>
                              <h5 className="mb-3">
                                {item.width} × {item.height} × {item.length}{item.unit === 'imperial' ? 'in' : 'cm'}
                                <br />
                                <strong>{item.weight}{item.unit === 'imperial' ? 'lb' : 'kg'}</strong>
                              </h5>
                              <Button
                                variant="link"
                                className="text-danger text-decoration-underline fw-bold"
                                onClick={() => removeItem(item.id)}
                              >
                                Remove
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center">
                          <span>{formData.originCity?.split(' (')[0] || 'Origin'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'Destination'}</span>
                        </div>
                        {selectedRoute && (
                          <small className="text-muted">
                            {selectedRoute.serviceLevel === 'EXPRESS' ? '⚡ Express' : '📦 Standard'} Service
                          </small>
                        )}
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.length === 0 ? (
                          <p className="text-muted mb-0">No items added yet</p>
                        ) : (
                          items.map((item, index) => {
                            // Calculate estimated price per item based on weight
                            const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                            let itemPrice = 0;
                            
                            // Use real UPS rates if available
                            if (upsRates && deliveryService) {
                              const serviceType = deliveryService === 'standard' ? 'ground' : 'express';
                              // Find closest weight tier
                              if (weightInLbs <= 23 && upsRates['23']) {
                                itemPrice = upsRates['23'][serviceType] || 0;
                              } else if (weightInLbs <= 50 && upsRates['50']) {
                                itemPrice = upsRates['50'][serviceType] || 0;
                              } else if (upsRates['73']) {
                                itemPrice = upsRates['73'][serviceType] || 0;
                              }
                            } else if (selectedRoute) {
                              // Fallback to route basePrice
                              if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                              else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                              else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                              else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                            }

                            return (
                              <div key={item.id} className="d-flex justify-content-between mb-2">
                                <span>• {item.itemType || 'Item'}</span>
                                <strong>${itemPrice.toFixed(2)}</strong>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Extras */}
                      {items.length > 0 && (
                        <div className="mb-3 pb-3 border-bottom">
                          <h6 className="mb-2">Extras</h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span>$200.00 Cover</span>
                            <span className="text-success">FREE</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>
                              Temporary Fuel Surcharge
                              <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                            </span>
                            <span>
                              ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                
                                if (upsRates && deliveryService) {
                                  const serviceType = deliveryService === 'standard' ? 'ground' : 'express';
                                  if (weightInLbs <= 23 && upsRates['23']) {
                                    itemPrice = upsRates['23'][serviceType] || 0;
                                  } else if (weightInLbs <= 50 && upsRates['50']) {
                                    itemPrice = upsRates['50'][serviceType] || 0;
                                  } else if (upsRates['73']) {
                                    itemPrice = upsRates['73'][serviceType] || 0;
                                  }
                                } else if (selectedRoute) {
                                  if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                  else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                  else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                  else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                }
                                return sum + itemPrice;
                              }, 0) * 0.13).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Total */}
                      {items.length > 0 && (selectedRoute || upsRates) && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                
                                if (upsRates && deliveryService) {
                                  const serviceType = deliveryService === 'standard' ? 'ground' : 'express';
                                  if (weightInLbs <= 23 && upsRates['23']) {
                                    itemPrice = upsRates['23'][serviceType] || 0;
                                  } else if (weightInLbs <= 50 && upsRates['50']) {
                                    itemPrice = upsRates['50'][serviceType] || 0;
                                  } else if (upsRates['73']) {
                                    itemPrice = upsRates['73'][serviceType] || 0;
                                  }
                                } else if (selectedRoute) {
                                  if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                  else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                  else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                  else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                }
                                return sum + itemPrice;
                              }, 0) * 1.13).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      {items.length > 0 && (
                        <Button
                          variant="success"
                          size="lg"
                          className="w-100 fw-bold"
                          onClick={continueFromAddItems}
                        >
                          Continue
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showCustomsAndInsurancePage ? (
            // Customs Information Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: true, completed: true },
                    { num: 2, label: 'Addresses', active: true, completed: false },
                    { num: 3, label: 'Dates', active: false, completed: false },
                    { num: 4, label: 'Essentials', active: false, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Customs Form */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowCustomsAndInsurancePage(false);
                      setShowItemsPage(true);
                      setCurrentStep(1);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-3">Customs Information</h3>
                  <p className="text-muted mb-4">
                    Please select how you would like your shipment declared to customs:
                  </p>

                  {/* Customs Declaration Options */}
                  <div className="mb-4">
                    <Card 
                      className={`mb-3 ${customsDeclaration === 'personal_own' ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCustomsDeclaration('personal_own')}
                    >
                      <Card.Body>
                        <Row>
                          <Col xs={2} className="text-center">
                            <i className="fas fa-suitcase fa-2x text-primary"></i>
                          </Col>
                          <Col xs={10}>
                            <h5>Personal Effects (My Own)</h5>
                            <p className="mb-2">
                              I am sending my own used personal effects and request Send My Bag declares my shipment to customs as such.
                            </p>
                            <a href="#" className="text-primary">More Info</a>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Card 
                      className={`mb-3 ${customsDeclaration === 'personal_other' ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCustomsDeclaration('personal_other')}
                    >
                      <Card.Body>
                        <Row>
                          <Col xs={2} className="text-center">
                            <i className="fas fa-users fa-2x text-primary"></i>
                          </Col>
                          <Col xs={10}>
                            <h5>Personal Effects (Someone Else's)</h5>
                            <p className="mb-2">
                              I am sending used personal effects on behalf of someone else and request Send My Bag declares my shipment to customs as such.
                            </p>
                            <a href="#" className="text-primary">More Info</a>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Card 
                      className={`mb-3 ${customsDeclaration === 'gift' ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCustomsDeclaration('gift')}
                    >
                      <Card.Body>
                        <Row>
                          <Col xs={2} className="text-center">
                            <i className="fas fa-gift fa-2x text-primary"></i>
                          </Col>
                          <Col xs={10}>
                            <h5>Gift</h5>
                            <p className="mb-2">
                              I am sending a gift package. I understand duties and taxes may be collected by the destination customs authority.
                            </p>
                            <a href="#" className="text-primary">More Info</a>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Card 
                      className={`mb-3 ${customsDeclaration === 'commercial' ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCustomsDeclaration('commercial')}
                    >
                      <Card.Body>
                        <Row>
                          <Col xs={2} className="text-center">
                            <i className="fas fa-truck fa-2x text-primary"></i>
                          </Col>
                          <Col xs={10}>
                            <h5>Commercial Items / Anything Else</h5>
                            <p className="mb-2">
                              I understand duties and taxes may be collected by the destination customs authority.
                            </p>
                            <a href="#" className="text-primary">More Info</a>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </div>

                  {/* Declared Value */}
                  <div className="mb-4">
                    <h5 className="mb-3">Declared Value For Customs</h5>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text">$</span>
                      <Form.Control
                        type="number"
                        value={declaredValue}
                        onChange={(e) => setDeclaredValue(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <a href="#" className="text-primary small">What is this? What should I enter?</a>
                  </div>

                  {/* Cover Section */}
                  <div className="mb-4">
                    <h5 className="mb-2">Cover</h5>
                    <p className="text-muted">Add cover to your order to protect against unexpected loss</p>
                    <Form.Group className="mb-3">
                      <Form.Label>Select the level of cover (per bag) you require</Form.Label>
                      <Form.Select
                        size="lg"
                        value={coverLevel}
                        onChange={(e) => {
                          setCoverLevel(e.target.value);
                          if (e.target.value !== 'custom') {
                            setCustomCoverageAmount('');
                          }
                        }}
                      >
                        <option value="">Please Select</option>
                        <option value="none">No Cover</option>
                        <option value="200">$200 Cover</option>
                        <option value="500">$500 Cover</option>
                        <option value="1000">$1000 Cover</option>
                        <option value="custom">Custom Amount</option>
                      </Form.Select>
                    </Form.Group>
                    
                    {coverLevel === 'custom' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Enter Custom Coverage Amount</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="Enter amount (e.g., 2500)"
                          value={customCoverageAmount}
                          onChange={(e) => setCustomCoverageAmount(e.target.value)}
                          min="100"
                          max="50000"
                          step="100"
                        />
                        <Form.Text className="text-muted">
                          Maximum coverage: $50,000. First $100 is free, then $0.90 per $100 of value.
                        </Form.Text>
                      </Form.Group>
                    )}
                    
                    <small className="text-muted">
                      UPS insurance does not replace external travel insurance appropriate to your own needs.
                    </small>
                  </div>
                </Col>

                {/* Right Column - Order Summary (same as before) */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center">
                          <span>{formData.originCity?.split(' (')[0] || 'Origin'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'Destination'}</span>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item, index) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                            
                            // Add extra volume charge if dimensions exceed 62 linear inches
                            const totalLinearInches = parseFloat(item.length || 0) + parseFloat(item.width || 0) + parseFloat(item.height || 0);
                            if (totalLinearInches > 62) {
                              itemPrice += 72.00;
                            }
                          }

                          return (
                            <div key={item.id} className="d-flex justify-content-between mb-2">
                              <span>• {item.itemType || 'Item'}</span>
                              <strong>${itemPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        {/* Coverage Display - Calculate insurance from UPS rate difference */}
                        {coverLevel && coverLevel !== 'none' && coverLevel !== '' && upsRates && upsRatesWithoutInsurance && items.length > 0 ? (
                          (() => {
                            // Get total weight to find correct tier
                            const totalWeight = items.reduce((sum, item) => {
                              const weight = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              return sum + weight;
                            }, 0);
                            
                            // Find closest weight tier
                            let weightTier = '50';
                            if (totalWeight <= 23) weightTier = '23';
                            else if (totalWeight <= 50) weightTier = '50';
                            else weightTier = '73';
                            
                            // Get UPS rates with and without insurance
                            const rateWithInsurance = upsRates[weightTier]?.ground || 0;
                            const rateWithoutInsurance = upsRatesWithoutInsurance[weightTier]?.ground || 0;
                            
                            // Insurance cost = difference between rates
                            const insuranceCost = rateWithInsurance - rateWithoutInsurance;
                            
                            return (
                              <div className="d-flex justify-content-between mb-2">
                                <span>
                                  Insurance ({coverLevel === 'custom' 
                                    ? `$${customCoverageAmount}` 
                                    : `$${coverLevel}`} coverage)
                                </span>
                                <span>
                                  ${insuranceCost > 0 ? insuranceCost.toFixed(2) : '0.00'}
                                </span>
                              </div>
                            );
                          })()
                        ) : null}
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Temporary Fuel Surcharge
                            <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13 + 
                              (coverLevel === '500' ? 10 : coverLevel === '1000' ? 20 : 0)).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <Button
                        variant="success"
                        size="lg"
                        className="w-100 fw-bold"
                        onClick={() => {
                          if (!customsDeclaration) {
                            toast.error('Please select a customs declaration');
                            return;
                          }
                          if (!declaredValue) {
                            toast.error('Please enter declared value');
                            return;
                          }
                          setShowCustomsAndInsurancePage(false);
                          setShowFromAddressPage(true);
                          setCurrentStep(2);
                        }}
                      >
                        Continue
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showFromAddressPage ? (
            // Address Collection Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: false, completed: true },
                    { num: 2, label: 'Addresses', active: true, completed: false },
                    { num: 3, label: 'Dates', active: false, completed: false },
                    { num: 4, label: 'Essentials', active: false, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: step.completed ? 'var(--primary-color)' : '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Address Form */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowFromAddressPage(false);
                      setShowCustomsAndInsurancePage(true);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-3">Where will we collect your items?</h3>
                  
                  {/* Type of Address */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Type of Address</Form.Label>
                    <Form.Select
                      size="lg"
                      value={addressType}
                      onChange={(e) => setAddressType(e.target.value)}
                    >
                      <option value="">Please Select</option>
                      <option value="hotel">Hotel</option>
                      <option value="residence">Residence</option>
                      <option value="business">Business</option>
                    </Form.Select>
                  </Form.Group>

                  {/* Hotel Warning */}
                  {addressType === 'hotel' && (
                    <Alert variant="warning" className="mb-4">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      If sending to or from a Hotel please ensure the "Guest Name" is in the format: "Mr Smith c/o The Reception" or "Mr Smith c/o The Concierge". When leaving your bags for collection please inform the hotel that a courier will be collecting them on your behalf.
                    </Alert>
                  )}

                  {/* Contact or Guest Name */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Contact or Guest Name</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={pickupContact}
                      onChange={(e) => setPickupContact(e.target.value)}
                      placeholder="aa bb"
                      style={USE_TEST_DEFAULTS && pickupContact === 'aa bb' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* Phone Number */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Phone Number of Contact</Form.Label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text">
                        <i className="fas fa-flag-usa me-1"></i> +1
                      </span>
                      <Form.Control
                        type="tel"
                        value={pickupPhone}
                        onChange={(e) => setPickupPhone(e.target.value)}
                        placeholder="4082026317"
                        style={USE_TEST_DEFAULTS && pickupPhone === '4082026317' ? { color: '#999' } : {}}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Unfortunately drivers cannot telephone upon arrival. Please ensure your address is fully accessible e.g. door bell working
                    </Form.Text>
                  </Form.Group>

                  {/* Zip Code */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Zip code</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={pickupZipCode}
                      onChange={(e) => setPickupZipCode(e.target.value)}
                      placeholder="94043"
                      style={USE_TEST_DEFAULTS && pickupZipCode === '94043' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* Hotel Name (optional) */}
                  {addressType === 'hotel' && (
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Hotel Name <span className="text-muted fw-normal">(optional)</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        size="lg"
                        value={pickupHotelName}
                        onChange={(e) => setPickupHotelName(e.target.value)}
                        placeholder="Enter hotel name"
                      />
                    </Form.Group>
                  )}

                  {/* Address Line 1 */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={pickupAddressLine1}
                      onChange={(e) => setPickupAddressLine1(e.target.value)}
                      placeholder="162 Cottonwood Ct"
                      style={USE_TEST_DEFAULTS && pickupAddressLine1 === '162 Cottonwood Ct' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* Address Line 2 */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      Address Line 2 <span className="text-muted fw-normal">(optional)</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={pickupAddressLine2}
                      onChange={(e) => setPickupAddressLine2(e.target.value)}
                      placeholder="Apartment, suite, etc."
                    />
                  </Form.Group>

                  {/* City */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">City</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={pickupCity}
                      onChange={(e) => setPickupCity(e.target.value)}
                      placeholder="Mountain View"
                      style={USE_TEST_DEFAULTS && pickupCity === 'Mountain View' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* State */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">State</Form.Label>
                    <Form.Select
                      size="lg"
                      value={pickupState}
                      onChange={(e) => setPickupState(e.target.value)}
                    >
                      <option value="">Please Select</option>
                      <option value="CA">California</option>
                      <option value="NY">New York</option>
                      <option value="TX">Texas</option>
                      <option value="FL">Florida</option>
                      <option value="IL">Illinois</option>
                      {/* Add more states as needed */}
                    </Form.Select>
                  </Form.Group>

                  {/* Country */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Country</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={pickupCountry}
                      disabled
                      style={{ backgroundColor: '#e9ecef' }}
                    />
                  </Form.Group>
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center">
                          <span>{formData.originCity?.split(' (')[0] || 'Origin'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'Destination'}</span>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                          }

                          return (
                            <div key={item.id} className="d-flex justify-content-between mb-2">
                              <span>• {item.itemType || 'Item'}</span>
                              <strong>${itemPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        <div className="d-flex justify-content-between mb-2">
                          <span>$200.00 Cover</span>
                          <span className="text-success">FREE</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Temporary Fuel Surcharge
                            <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <Button
                        variant="success"
                        size="lg"
                        className="w-100 fw-bold"
                        onClick={() => {
                          if (!addressType || !pickupContact || !pickupPhone || !pickupZipCode || !pickupAddressLine1 || !pickupCity || !pickupState) {
                            toast.error('Please fill in all required fields');
                            return;
                          }
                          setShowFromAddressPage(false);
                          setShowToAddressPage(true);
                        }}
                      >
                        Continue
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showToAddressPage ? (
            // Delivery Address Collection Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: false, completed: true },
                    { num: 2, label: 'Addresses', active: true, completed: false },
                    { num: 3, label: 'Dates', active: false, completed: false },
                    { num: 4, label: 'Essentials', active: false, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: step.completed ? 'var(--primary-color)' : '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Delivery Address Form */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowToAddressPage(false);
                      setShowFromAddressPage(true);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-3">Where will we deliver them to?</h3>
                  
                  {/* Type of Address */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Type of Address</Form.Label>
                    <Form.Select
                      size="lg"
                      value={deliveryAddressType}
                      onChange={(e) => setDeliveryAddressType(e.target.value)}
                    >
                      <option value="">Please Select</option>
                      <option value="hotel">Hotel</option>
                      <option value="residence">Residence</option>
                      <option value="business">Business</option>
                    </Form.Select>
                  </Form.Group>

                  {/* Hotel Warning */}
                  {deliveryAddressType === 'hotel' && (
                    <Alert variant="warning" className="mb-4">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      If sending to or from a Hotel please ensure the "Guest Name" is in the format: "Mr Smith c/o The Reception" or "Mr Smith c/o The Concierge". When leaving your bags for collection please inform the hotel that a courier will be collecting them on your behalf.
                    </Alert>
                  )}

                  {/* Contact or Guest Name */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Contact or Guest Name</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={deliveryContact}
                      onChange={(e) => setDeliveryContact(e.target.value)}
                      placeholder="rr ss"
                      style={USE_TEST_DEFAULTS && deliveryContact === 'rr ss' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* Phone Number */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Phone Number of Contact</Form.Label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text">
                        <i className="fas fa-flag-usa me-1"></i> +1
                      </span>
                      <Form.Control
                        type="tel"
                        value={deliveryPhone}
                        onChange={(e) => setDeliveryPhone(e.target.value)}
                        placeholder="4082026317"
                        style={USE_TEST_DEFAULTS && deliveryPhone === '4082026317' ? { color: '#999' } : {}}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Unfortunately drivers cannot telephone upon arrival. Please ensure your address is fully accessible e.g. door bell working
                    </Form.Text>
                  </Form.Group>

                  {/* Zip Code */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Zip code</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={deliveryZipCode}
                      onChange={(e) => setDeliveryZipCode(e.target.value)}
                      placeholder="10001"
                      style={USE_TEST_DEFAULTS && deliveryZipCode === '10001' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* Hotel Name (optional) */}
                  {deliveryAddressType === 'hotel' && (
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Hotel Name <span className="text-muted fw-normal">(optional)</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        size="lg"
                        value={deliveryHotelName}
                        onChange={(e) => setDeliveryHotelName(e.target.value)}
                        placeholder="Enter hotel name"
                      />
                    </Form.Group>
                  )}

                  {/* Address Line 1 */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={deliveryAddressLine1}
                      onChange={(e) => setDeliveryAddressLine1(e.target.value)}
                      placeholder="20 Cooper Square"
                      style={USE_TEST_DEFAULTS && deliveryAddressLine1 === '20 Cooper Square' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* Address Line 2 */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      Address Line 2 <span className="text-muted fw-normal">(optional)</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={deliveryAddressLine2}
                      onChange={(e) => setDeliveryAddressLine2(e.target.value)}
                      placeholder="Apartment, suite, etc."
                    />
                  </Form.Group>

                  {/* City */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">City</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={deliveryCity}
                      onChange={(e) => setDeliveryCity(e.target.value)}
                      placeholder="New York"
                      style={USE_TEST_DEFAULTS && deliveryCity === 'New York' ? { color: '#999' } : {}}
                    />
                  </Form.Group>

                  {/* State */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">State</Form.Label>
                    <Form.Select
                      size="lg"
                      value={deliveryState}
                      onChange={(e) => setDeliveryState(e.target.value)}
                    >
                      <option value="">Please Select</option>
                      <option value="CA">California</option>
                      <option value="NY">New York</option>
                      <option value="TX">Texas</option>
                      <option value="FL">Florida</option>
                      <option value="IL">Illinois</option>
                      {/* Add more states as needed */}
                    </Form.Select>
                  </Form.Group>

                  {/* Country */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Country</Form.Label>
                    <Form.Control
                      type="text"
                      size="lg"
                      value={deliveryCountry}
                      disabled
                      style={{ backgroundColor: '#e9ecef' }}
                    />
                  </Form.Group>
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center">
                          <span>{formData.originCity?.split(' (')[0] || 'Origin'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'Destination'}</span>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                          }

                          return (
                            <div key={item.id} className="d-flex justify-content-between mb-2">
                              <span>• {item.itemType || 'Item'}</span>
                              <strong>${itemPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        <div className="d-flex justify-content-between mb-2">
                          <span>$200.00 Cover</span>
                          <span className="text-success">FREE</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Temporary Fuel Surcharge
                            <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <Button
                        variant="success"
                        size="lg"
                        className="w-100 fw-bold"
                        onClick={() => {
                          if (!deliveryAddressType || !deliveryContact || !deliveryPhone || !deliveryZipCode || !deliveryAddressLine1 || !deliveryCity || !deliveryState) {
                            toast.error('Please fill in all required fields');
                            return;
                          }
                          setShowToAddressPage(false);
                          setShowReviewPage(true);
                        }}
                      >
                        Continue
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showReviewPage ? (
            // Review Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: false, completed: true },
                    { num: 2, label: 'Addresses', active: false, completed: true },
                    { num: 3, label: 'Dates', active: true, completed: false },
                    { num: 4, label: 'Essentials', active: false, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: step.completed ? 'var(--primary-color)' : '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Review Content */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowReviewPage(false);
                      setShowToAddressPage(true);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-4">Review your collection and delivery addresses:</h3>

                  {/* Collection and Delivery Cards */}
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card className="h-100">
                        <Card.Body className="text-center">
                          <h5 className="mb-3">Collection</h5>
                          <p className="mb-1"><strong>{pickupContact}</strong></p>
                          <p className="mb-1">{pickupAddressLine1}</p>
                          <p className="mb-3">{pickupCity}, {pickupZipCode}</p>
                          <Button variant="link" className="text-decoration-underline p-0">
                            Change
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="h-100">
                        <Card.Body className="text-center">
                          <h5 className="mb-3">Delivery</h5>
                          <p className="mb-1"><strong>{deliveryContact}</strong></p>
                          <p className="mb-1">{deliveryAddressLine1}</p>
                          <p className="mb-3">{deliveryCity}, {deliveryZipCode}</p>
                          <Button variant="link" className="text-decoration-underline p-0">
                            Change
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Journey Type Selection */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-3">You selected:</h5>
                      <Form.Check
                        type="radio"
                        label="One-Way Journey"
                        name="journeyType"
                        value="one-way"
                        checked={journeyType === 'one-way'}
                        onChange={(e) => setJourneyType(e.target.value)}
                        className="mb-2"
                      />
                      <Form.Check
                        type="radio"
                        label="Return Journey"
                        name="journeyType"
                        value="return"
                        checked={journeyType === 'return'}
                        onChange={(e) => setJourneyType(e.target.value)}
                      />
                    </Card.Body>
                  </Card>

                  {/* Flexi Section */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-2">Flexi</h5>
                      <p className="text-muted mb-3">Own the ability to modify or cancel your order</p>
                      <Form.Group>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="mb-0">
                            Cancellation and Change Cover $33.00
                            <i className="fas fa-info-circle ms-2 text-primary"></i>
                          </Form.Label>
                        </div>
                        <Form.Select
                          size="lg"
                          value={flexiCover}
                          onChange={(e) => setFlexiCover(e.target.value)}
                        >
                          <option value="">Please Select</option>
                          <option value="yes">Yes - Add Flexi Cover ($33.00)</option>
                          <option value="no">No - Continue without Flexi</option>
                        </Form.Select>
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  {/* Tracking Section */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-2">Tracking</h5>
                      <p className="text-muted small mb-3">
                        Once your order is complete we recommend downloading the free Send My Bag App to track your bag every step of the way. Or add your mobile number below to get text message updates:
                      </p>
                      <Form.Group>
                        <Form.Label>Phone Number $2.50 (optional)</Form.Label>
                        <div className="input-group input-group-lg">
                          <span className="input-group-text">
                            <i className="fas fa-flag-usa me-1"></i> +1
                          </span>
                          <Form.Control
                            type="tel"
                            value={trackingPhone}
                            onChange={(e) => setTrackingPhone(e.target.value)}
                            placeholder="Enter local number"
                          />
                        </div>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center">
                          <span>{formData.originCity?.split(' (')[0] || 'Origin'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'Destination'}</span>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                          }

                          return (
                            <div key={item.id} className="d-flex justify-content-between mb-2">
                              <span>• {item.itemType || 'Item'}</span>
                              <strong>${itemPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        <div className="d-flex justify-content-between mb-2">
                          <span>$200.00 Cover</span>
                          <span className="text-success">FREE</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Temporary Fuel Surcharge
                            <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13 + 
                              (flexiCover === 'yes' ? 33 : 0) + 
                              (trackingPhone ? 2.5 : 0)).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <Button
                        variant="success"
                        size="lg"
                        className="w-100 fw-bold"
                        onClick={() => {
                          setShowReviewPage(false);
                          setShowDatesPage(true);
                          setCurrentStep(3);
                        }}
                      >
                        Continue
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showDatesPage ? (
            // Dates Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: false, completed: true },
                    { num: 2, label: 'Addresses', active: false, completed: true },
                    { num: 3, label: 'Dates', active: true, completed: false },
                    { num: 4, label: 'Essentials', active: false, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: step.completed ? 'var(--primary-color)' : '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Calendar and Options */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowDatesPage(false);
                      setShowReviewPage(true);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-3">When should we collect your items?</h3>
                  <p className="text-muted mb-4">Select your collection date from {pickupAddressLine1}</p>

                  {/* Calendar */}
                  <Card className="mb-4">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <Button variant="link" className="text-decoration-none p-0" onClick={goToPreviousMonth}>
                          <i className="fas fa-chevron-left"></i>
                        </Button>
                        <h5 className="mb-0">
                          {new Date(calendarYear, calendarMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h5>
                        <Button variant="link" className="text-decoration-none p-0" onClick={goToNextMonth}>
                          <i className="fas fa-chevron-right"></i>
                        </Button>
                      </div>

                      {/* Calendar Grid */}
                      <div className="calendar-grid">
                        <style>{`
                          .calendar-grid {
                            display: grid;
                            grid-template-columns: repeat(7, 1fr);
                            gap: 8px;
                          }
                          .calendar-day-header {
                            text-align: center;
                            font-weight: 600;
                            font-size: 0.875rem;
                            color: #6c757d;
                            padding: 8px;
                          }
                          .calendar-day {
                            aspect-ratio: 1;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border: 1px solid #dee2e6;
                            border-radius: 4px;
                            cursor: pointer;
                            transition: all 0.2s;
                            font-size: 0.875rem;
                          }
                          .calendar-day:hover:not(.disabled):not(.empty):not(.selected) {
                            background-color: #e7f3f5;
                            border-color: var(--primary-color);
                          }
                          .calendar-day.selected {
                            background-color: var(--primary-color) !important;
                            color: white !important;
                            border-color: var(--primary-color) !important;
                            font-weight: bold;
                          }
                          .calendar-day.disabled {
                            color: #dee2e6;
                            cursor: not-allowed;
                            background-color: #f8f9fa;
                          }
                          .calendar-day.empty {
                            border: none;
                            cursor: default;
                          }
                        `}</style>

                        {/* Day headers */}
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="calendar-day-header">{day}</div>
                        ))}

                        {/* Calendar days */}
                        {generateCalendarDays().map((day, idx) => {
                          if (day === null) {
                            return <div key={`empty-${idx}`} className="calendar-day empty"></div>;
                          }

                          const now = new Date();
                          const currentDate = new Date(calendarYear, calendarMonth, day);
                          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                          const isDisabled = currentDate < today;
                          const isSelected = collectionDate === day && 
                                            calendarMonth === new Date().getMonth() && 
                                            calendarYear === new Date().getFullYear();

                          return (
                            <div
                              key={day}
                              className={`calendar-day ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                              onClick={() => !isDisabled && setCollectionDate(day)}
                            >
                              {day}
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Delivery Service */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-3">Delivery Service</h5>
                      <p className="text-muted mb-3">You have selected:</p>
                      <Form.Check
                        type="radio"
                        label="Standard"
                        name="deliveryService"
                        value="standard"
                        checked={deliveryService === 'standard'}
                        onChange={(e) => setDeliveryService(e.target.value)}
                        className="mb-2"
                      />
                      <Form.Check
                        type="radio"
                        label="Express"
                        name="deliveryService"
                        value="express"
                        checked={deliveryService === 'express'}
                        onChange={(e) => setDeliveryService(e.target.value)}
                      />
                    </Card.Body>
                  </Card>

                  {/* Collection Date Details - Show when date is selected */}
                  {collectionDate && (
                    <>
                      {/* Collection Date Card */}
                      <Card className="mb-4 bg-light">
                        <Card.Body>
                          <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                            COLLECTION DATE
                          </h6>
                          <h4 className="text-primary mb-3">
                            {collectionDate} {new Date(calendarYear, calendarMonth).toLocaleDateString('en-US', { month: 'long' })} {calendarYear}
                          </h4>
                          
                          <h6 className="text-uppercase text-muted mb-2 mt-4" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                            EXPECTED COLLECTION TIME
                          </h6>
                          <p className="mb-2 small">
                            Standard collection times are 9am-6pm. You may pick a preferred time slot below, however we cannot guarantee that it will be observed.
                          </p>
                          <Form.Select
                            size="lg"
                            value={collectionTime}
                            onChange={(e) => setCollectionTime(e.target.value)}
                          >
                            <option value="">Select time</option>
                            <option value="9-12">9am - 12pm</option>
                            <option value="12-15">12pm - 3pm</option>
                            <option value="15-18">3pm - 6pm</option>
                          </Form.Select>
                        </Card.Body>
                      </Card>

                      {/* Estimated Delivery Date Card */}
                      <Card className="mb-4 bg-light">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                              ESTIMATED <strong>STANDARD</strong> DELIVERY DATE
                            </h6>
                            <i className="fas fa-info-circle text-primary"></i>
                          </div>
                          <h4 className="text-primary mb-3">
                            {(() => {
                              const startDate = new Date(calendarYear, calendarMonth, collectionDate);
                              startDate.setDate(startDate.getDate() + 4);
                              const endDate = new Date(calendarYear, calendarMonth, collectionDate);
                              endDate.setDate(endDate.getDate() + 7);
                              
                              const formatDate = (date) => {
                                const day = date.getDate();
                                const month = date.toLocaleDateString('en-US', { month: 'long' });
                                const year = date.getFullYear();
                                return `${day} ${month} ${year}`;
                              };
                              
                              return `Between ${formatDate(startDate)} to ${formatDate(endDate)}`;
                            })()}
                          </h4>
                          
                          <h6 className="text-uppercase text-muted mb-2 mt-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                            EXPECTED <strong>STANDARD</strong> DELIVERY TIME
                          </h6>
                          <h5 className="text-primary mb-0">9am - 6pm</h5>
                        </Card.Body>
                      </Card>

                      {/* Warning Alert */}
                      <Alert variant="warning" className="mb-4">
                        <div className="d-flex">
                          <i className="fas fa-exclamation-circle me-3 mt-1"></i>
                          <div>
                            <p className="mb-2">
                              <strong>We have been unable to perform a postcode to postcode transit time check.</strong>
                            </p>
                            <p className="mb-2">
                              The estimated delivery date displayed assumes capital to capital. If you are sending outside of the capital city please be aware delivery may take longer, you can contact us for more information.
                            </p>
                            <p className="mb-0">
                              We will attempt a postcode to postcode check again when your labels generate, if your estimated transit time updates you will be notified by email.
                            </p>
                          </div>
                        </div>
                      </Alert>
                    </>
                  )}
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center mb-2">
                          <span>{formData.originCity?.split(' (')[0] || 'United States'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'UK'}</span>
                        </div>
                        {collectionDate && (
                          <div className="mt-2">
                            <small className="text-muted">Collection Date</small>
                            <div>
                              <strong>
                                {(() => {
                                  const date = new Date(calendarYear, calendarMonth, collectionDate);
                                  const day = date.getDate();
                                  const month = date.toLocaleDateString('en-US', { month: 'long' });
                                  const year = date.getFullYear();
                                  return `${day} ${month} ${year}`;
                                })()}
                              </strong>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                          }

                          return (
                            <div key={item.id} className="d-flex justify-content-between mb-2">
                              <span>• {item.itemType || 'Item'}</span>
                              <strong>${itemPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        <div className="d-flex justify-content-between mb-2">
                          <span>$200.00 Cover</span>
                          <span className="text-success">FREE</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Temporary Fuel Surcharge
                            <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <Button
                        variant="success"
                        size="lg"
                        className="w-100 fw-bold"
                        onClick={() => {
                          if (!collectionDate) {
                            toast.error('Please select a collection date');
                            return;
                          }
                          setShowDatesPage(false);
                          setShowEssentialsPage(true);
                          setCurrentStep(4);
                        }}
                      >
                        Continue
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showEssentialsPage ? (
            // Essentials Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: false, completed: true },
                    { num: 2, label: 'Addresses', active: false, completed: true },
                    { num: 3, label: 'Dates', active: false, completed: true },
                    { num: 4, label: 'Order', active: true, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: step.completed ? 'var(--primary-color)' : '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Essentials Form */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowEssentialsPage(false);
                      setShowDatesPage(true);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-4">Essentials</h3>

                  {/* Labels Section */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-3">Labels</h5>
                      <p className="mb-0">We will deliver labels to you, one label per bag. You will be required to attach these labels to your bags.</p>
                    </Card.Body>
                  </Card>

                  {/* Why are you using us? Section */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-3">Why are you using us?</h5>
                      <Form.Select
                        size="lg"
                        value={whyUsingUs}
                        onChange={(e) => setWhyUsingUs(e.target.value)}
                      >
                        <option value="">Please Select</option>
                        <option value="vacation">Vacation / Holiday</option>
                        <option value="business">Business Travel</option>
                        <option value="moving">Moving / Relocation</option>
                        <option value="student">Student Travel</option>
                        <option value="sports">Sports Equipment</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center mb-2">
                          <span>{formData.originCity?.split(' (')[0] || 'United States'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'UK'}</span>
                        </div>
                        {collectionDate && (
                          <div className="mt-2">
                            <small className="text-muted">Collection Date</small>
                            <div>
                              <strong>
                                {(() => {
                                  const date = new Date(calendarYear, calendarMonth, collectionDate);
                                  const day = date.getDate();
                                  const month = date.toLocaleDateString('en-US', { month: 'long' });
                                  const year = date.getFullYear();
                                  return `${day} ${month} ${year}`;
                                })()}
                              </strong>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                          }

                          return (
                            <div key={item.id} className="d-flex justify-content-between mb-2">
                              <span>• {item.itemType || 'Item'}</span>
                              <strong>${itemPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        <div className="d-flex justify-content-between mb-2">
                          <span>$200.00 Cover</span>
                          <span className="text-success">FREE</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Temporary Fuel Surcharge
                            <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <Button
                        variant="success"
                        size="lg"
                        className="w-100 fw-bold"
                        onClick={() => {
                          setShowEssentialsPage(false);
                          setShowConfirmOrderPage(true);
                        }}
                      >
                        Continue
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : showConfirmOrderPage ? (
            // Confirm Order Page
            <>
              {/* Progress Stepper */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { num: 1, label: 'Items', active: false, completed: true },
                    { num: 2, label: 'Addresses', active: false, completed: true },
                    { num: 3, label: 'Dates', active: false, completed: true },
                    { num: 4, label: 'Order', active: true, completed: false }
                  ].map((step, idx) => (
                    <div key={step.num} className="d-flex align-items-center flex-fill">
                      <div className="text-center">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: step.completed || step.active ? 'var(--primary-color)' : '#e9ecef',
                            color: step.completed || step.active ? 'white' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {step.num}
                        </div>
                        <small className={step.active ? 'text-primary fw-bold' : 'text-muted'}>
                          {step.label}
                        </small>
                      </div>
                      {idx < 3 && (
                        <div 
                          className="flex-fill mx-2"
                          style={{
                            height: '2px',
                            backgroundColor: step.completed ? 'var(--primary-color)' : '#e9ecef',
                            marginTop: '-20px'
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Row>
                {/* Left Column - Confirmation Form */}
                <Col lg={7}>
                  <Button 
                    variant="link" 
                    className="text-decoration-none mb-3 p-0"
                    onClick={() => {
                      setShowConfirmOrderPage(false);
                      setShowEssentialsPage(true);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    <strong>Back</strong>
                  </Button>

                  <h3 className="mb-4">Confirm your Order</h3>

                  {/* Confirm Section */}
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="mb-3">Confirm</h5>
                      
                      {/* Customs Checkbox */}
                      <Form.Check 
                        type="checkbox"
                        id="agreeCustoms"
                        className="mb-3"
                        checked={agreeCustoms}
                        onChange={(e) => setAgreeCustoms(e.target.checked)}
                        label={
                          <span>
                            I am sending used personal effects and request BagAboard to classify my shipment as such. I have read and understood the customs information displayed on the <a href={`/destinations/${deliveryCountry.toLowerCase().replace(/\s+/g, '-')}/?min=1&from=${pickupCountry}`} target="_blank" rel="noopener noreferrer">{deliveryCountry}</a> page.
                          </span>
                        }
                      />

                      {/* Aerosols Checkbox */}
                      <Form.Check 
                        type="checkbox"
                        id="agreeAerosols"
                        className="mb-3"
                        checked={agreeAerosols}
                        onChange={(e) => setAgreeAerosols(e.target.checked)}
                        label={
                          <span>
                            I agree to not send <strong>aerosols</strong>, perfume, nail varnish or other alcohol based / flammable liquids (due to heightened aviation security your bag will be held indefinitely if an x-ray reveals these items). Non flammable toiletries such as shower gel and toothpaste are permitted. I will comply with BagAboard's <a href="/faq" target="_blank">battery policy</a>. I will secure my bags with a cable tie rather than a lock.
                          </span>
                        }
                      />

                      {/* Terms and Conditions Checkbox */}
                      <Form.Check 
                        type="checkbox"
                        id="agreeTerms"
                        className="mb-3"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        label={
                          <span>
                            I have read and agree to the BagAboard <a href="/terms-and-conditions" target="_blank">terms and conditions</a>.
                          </span>
                        }
                      />

                      {/* Route Notes Checkbox */}
                      <Form.Check 
                        type="checkbox"
                        id="agreeRouteNotes"
                        className="mb-0"
                        checked={agreeRouteNotes}
                        onChange={(e) => setAgreeRouteNotes(e.target.checked)}
                        label="I agree that I have read and understood the following important notes related to my booking based on my chosen route:"
                      />
                    </Card.Body>
                  </Card>

                  {/* Route Alerts */}
                  <Alert variant="warning" className="mb-4">
                    <h5 className="text-danger mb-3">Route Alerts</h5>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-exclamation-circle text-danger me-2 mt-1"></i>
                      <span>Knives may not be sent on the service.</span>
                    </div>
                  </Alert>

                  {/* Error Message if not all agreed */}
                  {(!agreeCustoms || !agreeAerosols || !agreeTerms || !agreeRouteNotes) && (
                    <Alert variant="danger" className="mb-4">
                      You must agree to our terms and conditions before selecting a payment method
                    </Alert>
                  )}

                  {/* Payment Section - Only show when all checkboxes are checked */}
                  {agreeCustoms && agreeAerosols && agreeTerms && agreeRouteNotes && (
                    <Card className="mb-4">
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <Form.Check
                            type="radio"
                            id="payByCard"
                            name="paymentMethod"
                            defaultChecked
                            className="me-2"
                          />
                          <label htmlFor="payByCard" className="mb-0 me-3">Pay by Card</label>
                          <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Mastercard-logo.png" alt="Mastercard" style={{ height: '24px', marginRight: '8px' }} />
                          <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" style={{ height: '24px' }} />
                        </div>

                        <h5 className="mb-3">Billing Address</h5>
                        <Form.Group className="mb-3">
                          <Form.Select
                            value={billingAddressOption}
                            onChange={(e) => setBillingAddressOption(e.target.value)}
                            required
                            isInvalid={!billingAddressOption}
                          >
                            <option value="">Select billing address</option>
                            <option value="collection">Same as collection address</option>
                            <option value="delivery">Same as delivery address</option>
                            <option value="new">Add New Billing Address</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Please select a billing address option
                          </Form.Control.Feedback>
                        </Form.Group>

                        {/* Show address form when "Add New Billing Address" is selected */}
                        {billingAddressOption === 'new' && (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>Address Line 1</Form.Label>
                              <Form.Control
                                type="text"
                                value={billingAddressLine1}
                                onChange={(e) => setBillingAddressLine1(e.target.value)}
                                placeholder="Enter address line 1"
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Address Line 2 <span className="text-muted">(optional)</span></Form.Label>
                              <Form.Control
                                type="text"
                                value={billingAddressLine2}
                                onChange={(e) => setBillingAddressLine2(e.target.value)}
                                placeholder="Enter address line 2"
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>City</Form.Label>
                              <Form.Control
                                type="text"
                                value={billingCity}
                                onChange={(e) => setBillingCity(e.target.value)}
                                placeholder="Enter city"
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Country</Form.Label>
                              <Form.Select
                                value={billingCountry}
                                onChange={(e) => setBillingCountry(e.target.value)}
                              >
                                <option value="">Please Select</option>
                                <option value="United States">United States</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="Canada">Canada</option>
                                <option value="Australia">Australia</option>
                              </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Zip code</Form.Label>
                              <Form.Control
                                type="text"
                                value={billingZipCode}
                                onChange={(e) => setBillingZipCode(e.target.value)}
                                placeholder="Enter zip code"
                              />
                            </Form.Group>
                          </>
                        )}

                        <h5 className="mb-3 mt-4">Payment Details</h5>
                        
                        {/* Stripe Payment Form */}
                        <Elements stripe={stripePromise}>
                          <StripePaymentForm
                            onPaymentSuccess={async (paymentMethod) => {
                              console.log('Payment method created:', paymentMethod.id);
                              
                              // Check if user is authenticated before proceeding
                              if (!isAuthenticated()) {
                                console.log('User not authenticated, showing auth modal');
                                // Save payment method and booking intent to localStorage
                                const bookingIntent = {
                                  paymentMethodId: paymentMethod.id,
                                  timestamp: new Date().toISOString()
                                };
                                localStorage.setItem('pendingBooking', JSON.stringify(bookingIntent));
                                setPendingBookingData(bookingIntent);
                                setShowAuthModal(true);
                                return; // Stop here until user logs in
                              }
                              
                              try {
                                console.log('Creating booking with data:', {
                                  items,
                                  pickupCountry,
                                  deliveryCountry,
                                  collectionDate
                                });
                                
                                // Calculate total price
                                const totalPrice = items.reduce((sum, item) => {
                                  const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                  let itemPrice = 0;
                                  if (selectedRoute) {
                                    if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                    else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                    else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                    else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                    
                                    // Add extra volume charge
                                    const totalLinearInches = parseFloat(item.length || 0) + parseFloat(item.width || 0) + parseFloat(item.height || 0);
                                    if (totalLinearInches > 62) {
                                      itemPrice += 72.00;
                                    }
                                  }
                                  return sum + itemPrice;
                                }, 0);
                                
                                // Add fuel surcharge (13%)
                                const totalWithSurcharge = totalPrice * 1.13;
                                
                                // Calculate total weight from items
                                const totalWeight = items.reduce((sum, item) => {
                                  const weight = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                  return sum + weight;
                                }, 0);
                                
                                // Helper function to get state abbreviation
                                const getStateAbbr = (stateName) => {
                                  const stateMap = {
                                    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
                                    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
                                    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
                                    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                                    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
                                    'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
                                    'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
                                    'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                                    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
                                    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
                                  };
                                  return stateMap[stateName] || stateName;
                                };
                                
                                const pickupStateAbbr = getStateAbbr(pickupState);
                                const deliveryStateAbbr = getStateAbbr(deliveryState);
                                
                                // Find or create route based on pickup/delivery locations and service level
                                let routeId = selectedRoute?.id;
                                if (!routeId) {
                                  // Try to find a matching route from the backend
                                  try {
                                    // Convert country names to match database format
                                    const originCountry = pickupCountry === 'United States' ? 'USA' : pickupCountry;
                                    const destinationCountry = deliveryCountry === 'United States' ? 'USA' : deliveryCountry;
                                    
                                    const routesResponse = await apiService.routes.searchRoutes(originCountry, destinationCountry);
                                    console.log('Routes response:', routesResponse);
                                    console.log('Routes response type:', typeof routesResponse);
                                    console.log('Routes array:', routesResponse.routes);
                                    console.log('Is array?', Array.isArray(routesResponse.routes));
                                    
                                    // Handle both array and object responses
                                    const routes = Array.isArray(routesResponse) ? routesResponse : (routesResponse.routes || []);
                                    
                                    console.log('Looking for route:', {
                                      pickupStateAbbr,
                                      deliveryStateAbbr,
                                      deliveryService: deliveryService.toUpperCase()
                                    });
                                    console.log('Available routes:', routes.map(r => ({
                                      id: r.routeId,
                                      originState: r.originState,
                                      destState: r.destinationState,
                                      serviceLevel: r.serviceLevel
                                    })));
                                    
                                    // Find route matching service level and states
                                    const matchingRoute = routes.find(r => 
                                      r.serviceLevel?.toUpperCase() === deliveryService.toUpperCase() &&
                                      r.originState === pickupStateAbbr &&
                                      r.destinationState === deliveryStateAbbr
                                    );
                                    
                                    console.log('Matching route:', matchingRoute);
                                    
                                    if (!matchingRoute) {
                                      toast.error(`No route available from ${pickupStateAbbr} to ${deliveryStateAbbr}. Please contact support.`);
                                      throw new Error('No matching route found');
                                    }
                                    
                                    routeId = matchingRoute.id || matchingRoute.routeId;
                                  } catch (error) {
                                    console.error('Could not find matching route:', error);
                                    throw error; // Don't proceed with booking
                                  }
                                }
                                
                                // Create booking - match backend DTO fields
                                const bookingData = {
                                  routeId: routeId,
                                  pickupDate: collectionDate ? new Date(calendarYear, calendarMonth, collectionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                  pickupAddress: pickupAddressLine1 + (pickupAddressLine2 ? ', ' + pickupAddressLine2 : ''),
                                  pickupCity: pickupCity,
                                  pickupState: pickupStateAbbr,
                                  pickupPostalCode: pickupZipCode,
                                  deliveryAddress: deliveryAddressLine1 + (deliveryAddressLine2 ? ', ' + deliveryAddressLine2 : ''),
                                  deliveryCity: deliveryCity,
                                  deliveryState: deliveryStateAbbr,
                                  deliveryPostalCode: deliveryZipCode,
                                  recipientName: deliveryContact,
                                  recipientPhone: deliveryPhone,
                                  numberOfBags: items.length,
                                  totalWeight: totalWeight.toFixed(2),
                                  specialInstructions: `Service Level: ${deliveryService}. Payment Method: ${paymentMethod.id}. Total Price: $${totalWithSurcharge.toFixed(2)}`,
                                  insuranceSelected: false
                                };
                                
                                console.log('Sending booking data:', bookingData);
                                
                                const response = await apiService.bookings.createBooking(bookingData);
                                
                                console.log('Booking created:', response);
                                toast.success('Booking created successfully!');
                                setLoading(false);
                                
                                // Navigate to dashboard
                                setTimeout(() => {
                                  navigate('/dashboard');
                                }, 1500);
                                
                              } catch (error) {
                                console.error('Booking creation error:', error);
                                console.error('Error details:', error.response?.data || error.message);
                                toast.error(error.response?.data?.message || 'Failed to create booking. Please try again.');
                                setLoading(false);
                              }
                            }}
                            loading={loading}
                            setLoading={setLoading}
                          />
                        </Elements>
                      </Card.Body>
                    </Card>
                  )}
                </Col>

                {/* Right Column - Order Summary */}
                <Col lg={5}>
                  <Card className="shadow-sm border-primary border-3 sticky-top" style={{ top: '20px' }}>
                    <Card.Header className="bg-white border-bottom">
                      <h4 className="mb-0">Your Order</h4>
                    </Card.Header>
                    <Card.Body>
                      {/* Route */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Route</h6>
                        <div className="d-flex align-items-center mb-2">
                          <span>{formData.originCity?.split(' (')[0] || 'United States'}</span>
                          <i className="fas fa-arrow-right mx-2 text-primary"></i>
                          <span>{formData.destinationCity?.split(' (')[0] || 'UK'}</span>
                        </div>
                        {collectionDate && (
                          <div className="mt-2">
                            <small className="text-muted">Collection Date</small>
                            <div>
                              <strong>
                                {(() => {
                                  const date = new Date(calendarYear, calendarMonth, collectionDate);
                                  const day = date.getDate();
                                  const month = date.toLocaleDateString('en-US', { month: 'long' });
                                  const year = date.getFullYear();
                                  return `${day} ${month} ${year}`;
                                })()}
                              </strong>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Items */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Items</h6>
                        {items.map((item) => {
                          const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                          let itemPrice = 0;
                          let extraVolumeCharge = 0;
                          
                          if (selectedRoute) {
                            if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                            else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                            else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                            else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                            
                            // Calculate extra volume charge if dimensions exceed standard
                            const totalLinearInches = parseFloat(item.length || 0) + parseFloat(item.width || 0) + parseFloat(item.height || 0);
                            if (totalLinearInches > 62) {
                              extraVolumeCharge = 72.00;
                            }
                          }

                          return (
                            <div key={item.id} className="mb-2">
                              <div className="d-flex justify-content-between">
                                <span>• {item.itemType || 'Box'}</span>
                                <strong>${itemPrice.toFixed(2)}</strong>
                              </div>
                              {extraVolumeCharge > 0 && (
                                <div className="text-muted small ms-3">
                                  <em>Includes:</em><br />
                                  ${extraVolumeCharge.toFixed(2)} for extra volume{' '}
                                  <a href="#" onClick={(e) => e.preventDefault()}>
                                    <i className="fas fa-external-link-alt small text-primary"></i>
                                  </a>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Extras */}
                      <div className="mb-3 pb-3 border-bottom">
                        <h6 className="mb-2">Extras</h6>
                        <div className="d-flex justify-content-between mb-2">
                          <span>$200.00 Cover</span>
                          <span className="text-success">FREE</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Fuel and ESS Surcharge{' '}
                            <a href="#" onClick={(e) => e.preventDefault()}>
                              <i className="fas fa-external-link-alt small text-primary"></i>
                            </a>
                          </span>
                          <span>
                            ${(items.reduce((sum, item) => {
                              const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                              let itemPrice = 0;
                              if (selectedRoute) {
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                              }
                              return sum + itemPrice;
                            }, 0) * 0.13).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      {selectedRoute && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Total</h5>
                            <h4 className="mb-0 text-primary">
                              USD ${(items.reduce((sum, item) => {
                                const weightInLbs = item.unit === 'imperial' ? parseFloat(item.weight) : parseFloat(item.weight) * 2.20462;
                                let itemPrice = 0;
                                if (weightInLbs <= 33) itemPrice = parseFloat(selectedRoute.basePrice);
                                else if (weightInLbs <= 44) itemPrice = parseFloat(selectedRoute.basePrice) + 20;
                                else if (weightInLbs <= 55) itemPrice = parseFloat(selectedRoute.basePrice) + 50;
                                else itemPrice = parseFloat(selectedRoute.basePrice) + 70;
                                return sum + itemPrice;
                              }, 0) * 1.13).toFixed(2)}
                            </h4>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            // Pricing Page (similar to SendMyBag quote page)
            <>
              {/* Header with route info */}
              <Card className="shadow-sm mb-4">
                <Card.Body className="bg-light">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <h4 className="mb-2">
                        <i className="fas fa-plane me-2 text-primary"></i>
                        {formData.originCity} → {formData.destinationCity}
                      </h4>
                      <p className="text-muted mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Select your shipment details below to see the final price
                      </p>
                    </Col>
                    <Col md={4} className="text-md-end">
                      <Button variant="outline-secondary" size="sm" onClick={backToRouteSelection}>
                        <i className="fas fa-arrow-left me-2"></i>
                        Change Route
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Service Level Selection - SendMyBag Style */}
              {routes.length > 0 && !selectedRoute && (
                <Row className="mb-4">
                  {routes
                    .sort((a, b) => {
                      // Standard on left (comes first), Express on right (comes second)
                      const aLevel = a.serviceLevel || a.service_level || 'STANDARD';
                      const bLevel = b.serviceLevel || b.service_level || 'STANDARD';
                      if (aLevel === 'STANDARD' && bLevel === 'EXPRESS') return -1;
                      if (aLevel === 'EXPRESS' && bLevel === 'STANDARD') return 1;
                      return 0;
                    })
                    .map(route => {
                      // Get service level - try multiple property names
                      let serviceLevel = 'STANDARD'; // default
                      if (route.serviceLevel) serviceLevel = route.serviceLevel;
                      else if (route.service_level) serviceLevel = route.service_level;
                      
                      // Normalize to uppercase and trim whitespace
                      serviceLevel = String(serviceLevel).toUpperCase().trim();
                      
                      const isStandard = serviceLevel === 'STANDARD';
                      const isExpress = serviceLevel === 'EXPRESS';
                      
                      const borderColor = isStandard ? 'primary' : 'success';
                      const buttonColor = isStandard ? 'success' : 'outline-success';
                      const displayName = isStandard ? 'Standard' : 'Express';
                      
                      // Weight tiers in lbs (converting from kg)
                      const weightTiers = [
                        { lbs: 33, kg: 15, price: route.basePrice },
                        { lbs: 44, kg: 20, price: (parseFloat(route.basePrice) + 20).toFixed(2) },
                        { lbs: 55, kg: 25, price: (parseFloat(route.basePrice) + 50).toFixed(2) },
                        { lbs: 66, kg: 30, price: (parseFloat(route.basePrice) + 70).toFixed(2) }
                      ];

                      return (
                        <Col md={6} key={route.id}>
                          <Card className={`h-100 border-${borderColor} border-3 shadow-sm`}>
                            <Card.Body className="p-4">
                              <div className="text-center mb-4">
                                <h2 className="fw-bold mb-2">{displayName}</h2>
                                <p className="text-muted mb-0">
                                  {route.estimatedDeliveryDays}-{route.estimatedDeliveryDays + 1} Working Days
                                </p>
                              </div>

                              <div className="mb-4">
                                <div className="text-end mb-2">
                                  <strong>Price per item</strong>
                                </div>
                                {weightTiers.map((tier, idx) => (
                                  <div key={idx} className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="text-muted">Up to {tier.lbs}lb</span>
                                    <h4 className="mb-0 fw-bold">${tier.price}</h4>
                                  </div>
                                ))}
                              </div>

                              <Button
                                variant={buttonColor}
                                size="lg"
                                className="w-100 fw-bold"
                                onClick={() => selectRoute(route)}
                              >
                                Book {displayName}
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                </Row>
              )}
            </>
          )}

        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to create this booking?</p>
          <p>You will be redirected to the payment page after confirmation.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowConfirmModal(false);
              submitBooking();
            }}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Confirm Booking'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Auth Modal - Login/Register at Checkout */}
      <AuthModal
        show={showAuthModal}
        onHide={() => setShowAuthModal(false)}
        onSuccess={() => {
          // After successful login/register, proceed with booking
          if (pendingBookingData) {
            // Restore booking data and continue
            toast.success('Welcome! Completing your booking...');
            // The booking will be submitted automatically after modal closes
          }
        }}
      />
    </Container>
  );
}

export default BookingFlow;
